// src/views/menage/ConsignesEditor.jsx — CHANTIER 42 Phase 4 (ADR-302)
//
// Composant PARTAGÉ de rédaction des CONSIGNES INTERNES ménage. Un seul
// composant, DEUX points de montage :
//   - ADMIN         → onglet « Checklists » d'un logement (là où il configure
//                     déjà pièces / photos_min / incidents : même geste métier).
//   - ORCHESTRATEUR → section repliable en bas de « Suivi équipe » (son écran de
//                     pilotage — pas de nouvel onglet).
//
// LECTURE  : RLS-directe (fetchInstructionsForScope) — admin via admin_select,
//            orchestrateur via orchestrateur_select_pool. Le composant ne
//            CONFÈRE aucun droit : il rend ce que la base laisse passer.
// ÉCRITURE : endpoints service_role (saveInstruction / archiveInstruction).
//
// ── TROIS ÉTATS DE LECTURE, TROIS RENDUS DISTINCTS (correctif QA B1) ────────
//   `available:false`          → bandeau « module non actif » + formulaire masqué
//                                (migration pas encore appliquée — leçon R5).
//   `error:true`               → bandeau D'ERREUR explicite + la liste n'affirme
//                                RIEN. Une lecture qui échoue (réseau, 500,
//                                GRANT/RLS) ne doit JAMAIS s'afficher « Aucune
//                                consigne pour l'instant » : ce serait le faux
//                                vide que cette phase prétend éviter, et le
//                                rédacteur croirait à une perte de données.
//   lecture OK                 → la liste, honnêtement (« aucune » si aucune).
//   (L'app AGENT, elle, reste SILENCIEUSE dans les trois cas d'échec —
//   asymétrie volontaire, cf. src/lib/menage-instructions-api.js.)
//
// RGPD : aide permanente sous le champ — on INFORME, on ne CENSURE PAS (décision
// de cadrage). Aucun filtre applicatif sur le texte.
//
// ── ARCHIVER vs SUPPRIMER ───────────────────────────────────────────────────
//   « Archiver » (réversible) est proposé à tout rédacteur.
//   « Supprimer définitivement » n'apparaît QUE pour l'ADMIN (`canDelete`), et
//   derrière une confirmation explicite. Décision Dimitri : le corps est du
//   texte libre, une donnée personnelle de voyageur y sera écrite par erreur un
//   jour, et il doit pouvoir l'effacer lui-même (RGPD). Le prop n'est qu'un
//   CONFORT D'AFFICHAGE : c'est le serveur (requireAdmin) qui décide vraiment.

import { useState, useEffect, useCallback, useMemo } from "react"
import { DS } from "../../lib/ui"
import {
  fetchInstructionsForScope,
  saveInstruction,
  archiveInstruction,
  deleteInstruction,
} from "../../lib/menage-instructions-api"
import { Info, AlertTriangle, Archive, RotateCcw, Pencil, Trash2, X, Loader2 } from "lucide-react"

const MAX_BODY = 2000
const MAX_TITLE = 120

// Libellé de portée d'une consigne (dérivé de la nullité des 2 colonnes — même
// raisonnement que la RLS ; aucune colonne `type` n'existe).
export function scopeLabel(row) {
  if (!row) return ""
  if (!row.logement_id) return "Transverse · tous logements"
  if (!row.target_agent_email) return "Permanente · tout agent du logement"
  return `Ciblée · ${row.target_agent_email}`
}

export default function ConsignesEditor({
  currentUser,
  logementId = null,
  logementOptions = [],
  agentOptions = [],
  canWriteTransverse = false,
  canDelete = false,
}) {
  const [rows, setRows] = useState([])
  const [available, setAvailable] = useState(true)
  // Troisième état : la lecture a ÉCHOUÉ (table présente, requête en erreur).
  // Distinct de `available:false` — et surtout distinct de « liste vide ».
  const [readError, setReadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null) // { ok: boolean, text: string }

  // Formulaire.
  const [editId, setEditId] = useState(null)
  const [formLogement, setFormLogement] = useState(logementId || "")
  const [formAgent, setFormAgent] = useState("")
  const [formTitle, setFormTitle] = useState("")
  const [formBody, setFormBody] = useState("")
  const [formPriority, setFormPriority] = useState("normale")

  const reload = useCallback(async () => {
    setLoading(true)
    const res = await fetchInstructionsForScope(logementId ? { logementId } : {})
    setRows(res.instructions)
    setAvailable(res.available)
    setReadError(!!res.error)
    setLoading(false)
  }, [logementId])

  // Reset dur au changement de compte (anti-rémanence multi-comptes dans le même
  // onglet — une consigne ciblée ne doit jamais survivre à un switch de compte).
  useEffect(() => {
    setRows([])
    setMsg(null)
    setEditId(null)
  }, [currentUser?.email])

  useEffect(() => {
    reload()
  }, [reload])

  function resetForm() {
    setEditId(null)
    setFormLogement(logementId || "")
    setFormAgent("")
    setFormTitle("")
    setFormBody("")
    setFormPriority("normale")
  }

  function startEdit(row) {
    setEditId(row.id)
    setFormLogement(row.logement_id || "")
    setFormAgent(row.target_agent_email || "")
    setFormTitle(row.title || "")
    setFormBody(row.body || "")
    setFormPriority(row.priority || "normale")
    setMsg(null)
  }

  async function handleSave(e) {
    e?.preventDefault?.()
    const body = (formBody || "").trim()
    if (body.length < 1) {
      setMsg({ ok: false, text: "La consigne ne peut pas être vide." })
      return
    }
    if (body.length > MAX_BODY) {
      setMsg({ ok: false, text: `La consigne dépasse ${MAX_BODY} caractères.` })
      return
    }
    setSaving(true)
    setMsg(null)
    try {
      await saveInstruction({
        id: editId || undefined,
        logementId: formLogement || null,
        targetAgentEmail: formAgent || null,
        title: (formTitle || "").trim() || null,
        body,
        priority: formPriority,
      })
      setMsg({ ok: true, text: editId ? "Consigne modifiée." : "Consigne enregistrée." })
      resetForm()
      await reload()
    } catch (err) {
      setMsg({ ok: false, text: err.message || String(err) })
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive(row, archived) {
    setSaving(true)
    setMsg(null)
    try {
      await archiveInstruction(row.id, archived)
      setMsg({ ok: true, text: archived ? "Consigne archivée." : "Consigne réactivée." })
      await reload()
    } catch (err) {
      setMsg({ ok: false, text: err.message || String(err) })
    } finally {
      setSaving(false)
    }
  }

  // SUPPRESSION DÉFINITIVE (ADMIN SEUL — décision Dimitri, RGPD). Irréversible :
  // la ligne est réellement supprimée en base, seule une trace « qui / quand »
  // subsiste, sans le contenu. Un 403 serveur remonte tel quel (le `canDelete`
  // n'est qu'un confort d'affichage — le serveur reste l'arbitre).
  async function handleDelete(row) {
    setSaving(true)
    setMsg(null)
    try {
      await deleteInstruction(row.id)
      setMsg({ ok: true, text: "Consigne supprimée définitivement." })
      if (editId === row.id) resetForm()
      await reload()
    } catch (err) {
      setMsg({ ok: false, text: err.message || String(err) })
    } finally {
      setSaving(false)
    }
  }

  const actives = useMemo(() => rows.filter((r) => !r.archived_at), [rows])
  const archivees = useMemo(() => rows.filter((r) => r.archived_at), [rows])

  // ── INERTIE : migration non appliquée → bandeau EXPLICITE, formulaire masqué.
  if (!loading && !available) {
    return (
      <div
        data-testid="consignes-inactive"
        style={{
          display: "flex",
          gap: 9,
          alignItems: "flex-start",
          padding: "12px 14px",
          background: DS.toolBg,
          color: DS.textSecondary,
          borderRadius: 10,
          fontSize: 12.5,
          lineHeight: 1.5,
          border: `0.5px solid ${DS.border}`,
        }}
      >
        <Info size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Module consignes non actif (migration non appliquée). Aucune consigne ne peut être
          rédigée pour l'instant — ce n'est <strong>pas</strong> une perte de données.
        </span>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: DS.fontBody, color: DS.marineDark }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "18px 4px",
            fontSize: 13,
            color: DS.textMuted,
          }}
        >
          <Loader2 size={15} /> Chargement des consignes…
        </div>
      ) : (
        <>
          {/* ── B1 — BANDEAU D'ERREUR DE LECTURE ──────────────────────────────
              La requête a échoué alors que le module EST actif. On le dit, et
              on n'affiche SURTOUT PAS « Aucune consigne pour l'instant » (ce
              serait faux, et ferait croire à une perte de données). Le
              formulaire reste ouvert : écrire une nouvelle consigne passe par
              l'endpoint, il n'a pas besoin que la lecture ait réussi. */}
          {readError && (
            <div
              data-testid="consignes-erreur-lecture"
              role="alert"
              style={{
                display: "flex",
                gap: 9,
                alignItems: "flex-start",
                padding: "11px 13px",
                marginBottom: 12,
                background: DS.dangerBg,
                color: DS.dangerText,
                borderRadius: 10,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Les consignes n'ont pas pu être chargées. La liste ci-dessous est{" "}
                <strong>incomplète</strong> — ce n'est <strong>pas</strong> une absence de
                consigne. Rechargez la page avant d'en ajouter une.
              </span>
            </div>
          )}

          {/* ── Liste ─────────────────────────────────────────────────────── */}
          {actives.length === 0 && archivees.length === 0 ? (
            readError ? null : (
              <div style={{ fontSize: 12.5, color: DS.textMuted, padding: "6px 0 12px" }}>
                Aucune consigne pour l'instant.
              </div>
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {[...actives, ...archivees].map((row) => (
                <ConsigneRow
                  key={row.id}
                  row={row}
                  busy={saving}
                  canDelete={canDelete}
                  onEdit={() => startEdit(row)}
                  onArchive={(archived) => handleArchive(row, archived)}
                  onDelete={() => handleDelete(row)}
                />
              ))}
            </div>
          )}

          {/* ── Formulaire ────────────────────────────────────────────────── */}
          <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {/* Sélecteur de logement — masqué quand le point de montage EST déjà
                un logement (onglet Checklists admin). */}
            {!logementId && (
              <label style={labelStyle}>
                Logement
                <select
                  value={formLogement}
                  onChange={(e) => setFormLogement(e.target.value)}
                  disabled={!!editId}
                  aria-label="Logement"
                  style={inputStyle}
                >
                  {/* La forme TRANSVERSE échappe au cloisonnement par logement :
                      elle est ADMIN ONLY (le serveur renvoie 403 sinon). On ne
                      propose donc même pas l'option à l'orchestrateur. */}
                  {canWriteTransverse ? (
                    <option value="">Transverse — tous logements</option>
                  ) : (
                    <option value="">— Choisir un logement —</option>
                  )}
                  {logementOptions.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nom || l.id}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label style={labelStyle}>
              Destinataire
              <select
                value={formAgent}
                onChange={(e) => setFormAgent(e.target.value)}
                disabled={!!editId}
                aria-label="Destinataire"
                style={inputStyle}
              >
                <option value="">Tout agent intervenant sur ce logement (permanente)</option>
                {agentOptions.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.label || a.email}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              Titre (facultatif)
              <input
                type="text"
                value={formTitle}
                maxLength={MAX_TITLE}
                onChange={(e) => setFormTitle(e.target.value)}
                aria-label="Titre de la consigne"
                placeholder="Ex. Vitre du salon"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Consigne
              <textarea
                value={formBody}
                maxLength={MAX_BODY}
                rows={3}
                onChange={(e) => setFormBody(e.target.value)}
                aria-label="Consigne"
                placeholder="Ex. La vitre du salon est fissurée — ne pas frotter."
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
              />
            </label>

            {/* Aide permanente RGPD — on informe, on ne filtre pas (cadrage).
                Rappelle aussi R10 : ciblée suit la PERSONNE, permanente suit le LIEU. */}
            <div
              data-testid="consignes-aide-rgpd"
              style={{
                fontSize: 11.5,
                color: DS.textSecondary,
                lineHeight: 1.55,
                background: DS.cielSoft,
                borderRadius: 8,
                padding: "9px 11px",
              }}
            >
              Consigne interne, visible uniquement de l'agent concerné. N'écrivez pas de nom,
              d'e-mail ni de téléphone de voyageur.
              <br />
              Une consigne <strong>ciblée</strong> suit la personne ; une consigne{" "}
              <strong>permanente</strong> suit le logement (elle survit à une réassignation).
            </div>

            <label style={labelStyle}>
              Priorité
              <select
                value={formPriority}
                onChange={(e) => setFormPriority(e.target.value)}
                aria-label="Priorité"
                style={inputStyle}
              >
                <option value="normale">Normale</option>
                <option value="importante">Importante</option>
              </select>
            </label>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
              <button
                type="submit"
                disabled={saving}
                className="fbz-press"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  background: DS.corail,
                  color: "#fff",
                  border: "none",
                  borderRadius: DS.radiusControl,
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: DS.fontBody,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {editId ? "Modifier la consigne" : "Ajouter la consigne"}
              </button>
              {editId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="fbz-press"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: DS.card,
                    color: DS.textSecondary,
                    border: `1px solid ${DS.border}`,
                    borderRadius: DS.radiusControl,
                    padding: "10px 14px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: DS.fontBody,
                  }}
                >
                  <X size={14} strokeWidth={2} /> Annuler
                </button>
              )}
            </div>
          </form>

          {msg && (
            <div
              role="status"
              style={{
                marginTop: 10,
                padding: "9px 12px",
                borderRadius: 8,
                fontSize: 12.5,
                background: msg.ok ? DS.successBg : DS.dangerBg,
                color: msg.ok ? DS.successText : DS.dangerText,
              }}
            >
              {msg.text}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Ligne de consigne (liste rédacteur) ────────────────────────────────────
function ConsigneRow({ row, busy, canDelete = false, onEdit, onArchive, onDelete }) {
  // Confirmation EN DEUX TEMPS, dans la ligne : la suppression est
  // irréversible, elle ne doit pas partir sur un clic distrait. Pas de
  // window.confirm (bloquant, non stylable, non testable proprement).
  const [confirming, setConfirming] = useState(false)
  const archived = !!row.archived_at
  const important = row.priority === "importante"
  return (
    <div
      style={{
        border: `0.5px solid ${DS.border}`,
        borderLeft: `3px solid ${important ? DS.corail : DS.ciel}`,
        borderRadius: 10,
        padding: "10px 12px",
        background: archived ? DS.toolBg : DS.card,
        opacity: archived ? 0.7 : 1,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: DS.textMuted, letterSpacing: ".04em" }}>
          {scopeLabel(row)}
        </span>
        {important && (
          <span style={{ fontSize: 10.5, fontWeight: 700, color: DS.corailText }}>Importante</span>
        )}
        {archived && (
          <span style={{ fontSize: 10.5, fontWeight: 700, color: DS.textMuted }}>Archivée</span>
        )}
      </div>
      {row.title && (
        <div style={{ fontWeight: 700, fontSize: 13.5, color: DS.marine, marginTop: 3 }}>
          {row.title}
        </div>
      )}
      <div style={{ fontSize: 13, color: DS.marineDark, marginTop: 2, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
        {row.body}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {!archived && (
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            style={miniBtnStyle}
            aria-label="Modifier la consigne"
          >
            <Pencil size={12} strokeWidth={2} /> Modifier
          </button>
        )}
        {/* ARCHIVER — geste ORDINAIRE et RÉVERSIBLE : la ligne reste en base. */}
        <button
          type="button"
          onClick={() => onArchive(!archived)}
          disabled={busy}
          style={miniBtnStyle}
          aria-label={archived ? "Réactiver la consigne" : "Archiver la consigne"}
        >
          {archived ? (
            <>
              <RotateCcw size={12} strokeWidth={2} /> Réactiver
            </>
          ) : (
            <>
              <Archive size={12} strokeWidth={2} /> Archiver
            </>
          )}
        </button>

        {/* SUPPRIMER DÉFINITIVEMENT — ADMIN SEUL (décision Dimitri, RGPD).
            Rendu UNIQUEMENT si `canDelete` ; le serveur (requireAdmin) reste
            l'arbitre réel, ce test n'est qu'un confort d'affichage. */}
        {canDelete &&
          (confirming ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false)
                  onDelete()
                }}
                disabled={busy}
                style={{ ...miniBtnStyle, color: DS.dangerText, borderColor: DS.dangerText }}
                aria-label="Confirmer la suppression définitive"
              >
                <Trash2 size={12} strokeWidth={2} /> Confirmer la suppression
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                style={miniBtnStyle}
                aria-label="Annuler la suppression"
              >
                <X size={12} strokeWidth={2} /> Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy}
              style={{ ...miniBtnStyle, color: DS.dangerText }}
              aria-label="Supprimer définitivement la consigne"
              title="Efface réellement la consigne. Seuls la date et l'auteur de la suppression sont conservés — jamais le texte."
            >
              <Trash2 size={12} strokeWidth={2} /> Supprimer
            </button>
          ))}
      </div>
      {canDelete && confirming && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: DS.dangerText, lineHeight: 1.5 }}>
          Suppression <strong>définitive</strong> : le texte de la consigne est effacé et ne
          pourra pas être récupéré. Seuls la date et l'auteur de la suppression sont conservés.
        </div>
      )}
    </div>
  )
}

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 11.5,
  fontWeight: 600,
  color: DS.textSecondary,
}

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  border: `1px solid ${DS.border}`,
  borderRadius: DS.radiusControl,
  fontSize: 13,
  fontFamily: DS.fontBody,
  color: DS.marineDark,
  background: DS.card,
  boxSizing: "border-box",
}

const miniBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: "transparent",
  color: DS.textSecondary,
  border: `1px solid ${DS.border}`,
  borderRadius: 7,
  padding: "5px 9px",
  fontSize: 11.5,
  cursor: "pointer",
  fontFamily: DS.fontBody,
}
